/**
 * Display formatters for the Transaction History drawer.
 *
 * Extracted from TransactionHistoryCard so the card is a shell: these are pure
 * string functions with no React or hook dependency, and both the row renderer
 * and the row builder need them.
 */

/**
 * Turn an XP ledger `subject` into something a player recognises.
 *
 * OVERWOLF is the internal name for in-game tracking and means nothing to a
 * user, so it reads as "Gaming".
 */
export function formatXPDescription(subject: string): string {
    if (!subject) {
        return "XP Transaction";
    }

    return subject
        .replace(/_/g, " ")
        .replace(/OVERWOLF/g, "Gaming")
        .replace(/GUILD/g, "Guild")
        .replace(/BRAND/g, "Brand")
        .toLowerCase()
        .replace(/\b\w/g, (l) => l.toUpperCase());
}

/**
 * Turn a Fuel ledger `subject` into something a player recognises.
 *
 * The four explicit cases are legacy subjects whose generic formatting reads
 * badly ("Fiat Purchase Fuel", "Mint Nft Purchase").
 */
export function formatTransactionDescription(subject: string): string {
    if (!subject) {
        return "Transaction";
    }

    if (subject === "FIAT_PURCHASE_FUEL") {
        return "Fuel Load";
    }
    if (subject === "MINT_NFT_PURCHASE") {
        return "LootBox Purchase";
    }
    if (subject === "LOOTBOX_BURNED") {
        return "LootBox Opening";
    }
    if (subject === "MENU_ORDER_PAYMENT") {
        return "Menu Order Payment";
    }

    return subject
        .replace(/_/g, " ")
        .replace(/FUEL/g, "Fuel")
        .toLowerCase()
        .replace(/\b\w/g, (l) => l.toUpperCase());
}

/** Abbreviate point balances so a 1.2B Fuel row does not blow out the column. */
export function formatLargeNumber(num: number): string {
    if (num >= 1_000_000_000) {
        return `${(num / 1_000_000_000).toFixed(2)}B`;
    }
    if (num >= 1_000_000) {
        return `${(num / 1_000_000).toFixed(2)}M`;
    }
    if (num >= 10_000) {
        return `${(num / 1_000).toFixed(2)}K`;
    }
    return num.toLocaleString();
}

/** Relative time for the last week, then an absolute short date. */
export function formatDate(date: Date | string | undefined): string {
    if (!date) {
        return "N/A";
    }
    const d = new Date(date);
    const diffHours = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60));

    if (diffHours < 1) {
        return "Just now";
    }
    if (diffHours < 24) {
        return `${diffHours}h ago`;
    }
    if (diffHours < 48) {
        return "Yesterday";
    }
    if (diffHours < 168) {
        return `${Math.floor(diffHours / 24)}d ago`;
    }

    return d.toLocaleDateString("en-US", {month: "short", day: "numeric"});
}
